import { Currency, Booking, ReadOnlyBooking } from 'hotdesk_typescript';
import { InlineResponse2001 } from 'hotdesk_typescript';
import { currencyConvert } from '@/hotdesk-ui/helpers/utils';

/** Booking price filters are compared against USD unified unit prices on the API. */
export const convertBookingPriceToUsd = (
    currencyList: Currency[],
    displayCurrencyCode: string,
    amount: number
): number => {
    if (!Number.isFinite(amount)) return 0;
    if (!displayCurrencyCode || displayCurrencyCode === 'USD') return amount;
    if (!currencyList?.length) return amount;
    return currencyConvert(currencyList, displayCurrencyCode, 'USD', amount);
};

export const convertBookingPriceFromUsd = (
    currencyList: Currency[],
    displayCurrencyCode: string,
    amount: number
): number => {
    if (!Number.isFinite(amount)) return 0;
    if (!displayCurrencyCode || displayCurrencyCode === 'USD') return amount;
    if (!currencyList?.length) return amount;
    return currencyConvert(currencyList, 'USD', displayCurrencyCode, amount);
};

export const hasBookingPriceFilter = (
    filters: Record<string, string>
): boolean =>
    Boolean(
        (filters.priceGte != null && filters.priceGte !== '') ||
            (filters.priceLte != null && filters.priceLte !== '')
    );

export const bookingMatchesPriceFilter = (
    booking: Booking | ReadOnlyBooking,
    filters: Record<string, string>,
    currencyList: Currency[]
): boolean => {
    if (!hasBookingPriceFilter(filters)) return true;

    const selectedPlans =
        filters.plans?.split(',').map((plan) => plan.trim()).filter(Boolean) ??
        [];
    const unit = booking.details?.unit;
    if (selectedPlans.length && unit && !selectedPlans.includes(unit)) {
        return false;
    }

    const rawUnitPrice = booking.details?.unitPrice;
    if (rawUnitPrice == null || !Number.isFinite(Number(rawUnitPrice))) {
        return false;
    }

    const bookingCurrency = booking.details?.currency ?? 'USD';
    const unitPriceUsd = convertBookingPriceToUsd(
        currencyList,
        bookingCurrency,
        Number(rawUnitPrice)
    );

    const min =
        filters.priceGte != null && filters.priceGte !== ''
            ? Number(filters.priceGte)
            : null;
    const max =
        filters.priceLte != null && filters.priceLte !== ''
            ? Number(filters.priceLte)
            : null;

    if (min != null && Number.isFinite(min) && unitPriceUsd < min) return false;
    if (max != null && Number.isFinite(max) && unitPriceUsd > max) return false;
    return true;
};

/** Client-side fallback when the booking list API ignores price_gte / price_lte. */
export const applyBookingListPriceFilter = (
    response: InlineResponse2001,
    filters: Record<string, string>,
    currencyList: Currency[]
): InlineResponse2001 => {
    if (!hasBookingPriceFilter(filters)) return response;

    const results = (response.results ?? []).filter((booking) =>
        bookingMatchesPriceFilter(booking, filters, currencyList)
    );

    return {
        ...response,
        results,
        count: results.length
    };
};
